import React, { useContext, useEffect, useState } from "react";
import { HorizontalNav } from "../components/HorizontalNav";
import { VerticalNav } from "../components/VerticalNav";
import { Question } from "../components/Question";
import "./MainPage.css";
import {
  CallOptionsType,
  GivenQuestions,
  LeaversType,
  Phases,
  QuestionType,
  SubPhases,
} from "../constants/Questions";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { CompanyName } from "../inputs/CompanyNameInput";
import { FoundersInput } from "../inputs/FoundersInput";
import { SharesInput } from "../inputs/SharesInput";
import { CommitmentInput } from "../inputs/CommitmentInput";
import { ResponsibilitiesInput } from "../inputs/ResponsibilitiesInput";
import { Deliverables } from "../results/Deliverables";
import ReactPDF from "@react-pdf/renderer";
import { ResultContext } from "../ResultContext";
import { LeaversInput } from "../inputs/LeaversInput";
import { VestingDurationInput } from "../inputs/VestingDurationInput";
import { StartDateInput } from "../inputs/StartDateInput";
import { VestingFrequencyInput } from "../inputs/VestingFrequencyInput";
import { CallOptionsInput } from "../inputs/CallOptionsInput";
import { VestingCliffInput } from "../inputs/VestingCliffInput";
import { VestingSharesInput } from "../inputs/VestingSharesInput";
import { BadAfterVestingInput } from "../inputs/BadAfterVestingInput";
import { VestingDuration } from "../constants/moreinformation/VestingDuration";
import { VestingStart } from "../constants/moreinformation/VestingStart";
import { VestingShares } from "../constants/moreinformation/VestingShares";
import { VestingCliff } from "../constants/moreinformation/VestingCliff";
import { VestingFrequency } from "../constants/moreinformation/VestingFrequency";
import { GoodLeaver } from "../constants/moreinformation/GoodLeaver";
import { EarlyLeaver } from "../constants/moreinformation/EarlyLeaver";
import { BadLeaver } from "../constants/moreinformation/BadLeaver";
import { BadAfterVesting } from "../constants/moreinformation/BadAfterVesting";
import { CallOptionsGood } from "../constants/moreinformation/CallOptionsGood";
import { CallOptionsEarly } from "../constants/moreinformation/CallOptionsEarly";
import { CallOptionsBad } from "../constants/moreinformation/CallOptionsBad";
import { TimeCommitment } from "../constants/moreinformation/TimeCommitment";
import { Responsibilities } from "../constants/moreinformation/Responsibilities";
import { GraphMaker } from "../results/GraphMaker";
import { Pdf } from "../Pdf";
import { DateTime } from "luxon";
import pdf = ReactPDF.pdf;
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { routes } from "../constants/Routes";

export function MainPage() {
  const { result, setResult } = useContext(ResultContext);
  const [selectedPhase, setSelectedPhase] = useState(0);
  const [selectedSubPhase, setSelectedSubPhase] = useState(0);
  const [image, setImage] = useState("");
  const location = useLocation();

  const sortedPhases = Phases.sort((a, b) => a.position - b.position);
  const allSubPhases = SubPhases.filter((p) => p.phase === selectedPhase).sort(
    (a, b) => a.position - b.position
  );

  const foundPhase = sortedPhases.find((p) => p.id === selectedPhase);
  const foundSubPhase = allSubPhases.find((p) => p.id === selectedSubPhase);
  const foundQuestion = GivenQuestions.find(
    (q) => q.phase === selectedPhase && q.subPhase === selectedSubPhase
  );

  const finalPhase = foundPhase
    ? foundPhase.position === Phases.length - 1
    : false;

  const navigate = useNavigate();

  useEffect(() => {
    if (!location.search) {
      return;
    }

    const urlSearchParams = new URLSearchParams(location.search);
    const document = urlSearchParams.get("documentId");

    if (!document) {
      return;
    }

    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/documents?documentId=${document}`
      )
      .then((result) => {
        if (result.data.content) {
          setResult(result.data.content);
          const foundFinalPhase = sortedPhases.find(
            (s) => s.position === Phases.length - 1
          );

          if (!foundFinalPhase) {
            return;
          }

          setSelectedPhase(foundFinalPhase.id);
        }
      });
  }, []);

  function goToPreviousPhase() {
    if (!foundPhase) {
      return;
    }

    setSelectedPhase(sortedPhases[foundPhase.position - 1].id);
  }

  function goToNextPhase() {
    if (!foundPhase) {
      return;
    }

    setSelectedPhase(sortedPhases[foundPhase.position + 1].id);
  }

  function goToNextSubPhase() {
    if (!foundSubPhase) {
      return;
    }

    setSelectedSubPhase(allSubPhases[foundSubPhase.position + 1].id);
  }

  function goBackWhenOnStart() {
    if (!foundPhase) {
      return;
    }

    const reversedPreviousAllSubPhases = SubPhases.filter(
      (p) => p.phase === sortedPhases[foundPhase.position - 1].id
    ).sort((a, b) => b.position - a.position);

    if (reversedPreviousAllSubPhases.length === 0) {
      goToPreviousPhase();
      return;
    }

    goToPreviousPhase();
    setSelectedSubPhase(reversedPreviousAllSubPhases[0].id);
  }

  function goNextWhenOnEnd() {
    if (!foundPhase) {
      return;
    }

    const nextAllSubPhases = SubPhases.filter(
      (p) => p.phase === sortedPhases[foundPhase.position + 1].id
    ).sort((a, b) => a.position - b.position);

    if (nextAllSubPhases.length === 0) {
      goToNextPhase();
      return;
    }

    goToNextPhase();
    setSelectedSubPhase(nextAllSubPhases[0].id);
  }

  function filterMoreInfo(foundQuestion: any) {
    if (foundQuestion.questionType === QuestionType.VestingDurationInput) {
      return VestingDuration;
    } else if (foundQuestion.questionType === QuestionType.VestingStartInput) {
      return VestingStart;
    } else if (foundQuestion.questionType === QuestionType.VestingSharesInput) {
      return VestingShares;
    } else if (
      foundQuestion.questionType === QuestionType.VestingFrequencyInput
    ) {
      return VestingFrequency;
    } else if (foundQuestion.questionType === QuestionType.VestingCliffInput) {
      return VestingCliff;
    } else if (
      foundQuestion.questionType === QuestionType.LeaversInput &&
      foundQuestion.type === LeaversType.Good
    ) {
      return GoodLeaver;
    } else if (
      foundQuestion.questionType === QuestionType.LeaversInput &&
      foundQuestion.type === LeaversType.Early
    ) {
      return EarlyLeaver;
    } else if (
      foundQuestion.questionType === QuestionType.LeaversInput &&
      foundQuestion.type === LeaversType.Bad
    ) {
      return BadLeaver;
    } else if (
      foundQuestion.questionType === QuestionType.BadAfterVestingInput
    ) {
      return BadAfterVesting;
    } else if (
      foundQuestion.questionType === QuestionType.CallOptionsInput &&
      foundQuestion.type === CallOptionsType.Good
    ) {
      return CallOptionsGood;
    } else if (
      foundQuestion.questionType === QuestionType.CallOptionsInput &&
      foundQuestion.type === CallOptionsType.Early
    ) {
      return CallOptionsEarly;
    } else if (
      foundQuestion.questionType === QuestionType.CallOptionsInput &&
      foundQuestion.type === CallOptionsType.Bad
    ) {
      return CallOptionsBad;
    } else if (foundQuestion.questionType === QuestionType.CommitmentInput) {
      return TimeCommitment;
    } else if (
      foundQuestion.questionType === QuestionType.ResponsibilitiesInput
    ) {
      return Responsibilities;
    } else {
      return <></>;
    }
  }

  return (
    <div className="main flex flex-column">
      <HorizontalNav
        selectedPhase={selectedPhase}
        setSelectedPhase={(p) => {
          const allSubPhases = SubPhases.filter((ph) => ph.phase === p).sort(
            (a, b) => a.position - b.position
          );

          if (allSubPhases.length === 0) {
            setSelectedPhase(p);
            return;
          }

          setSelectedSubPhase(allSubPhases[0].id);

          setSelectedPhase(p);
        }}
      />
      <div className="flex content flex-column">
        {foundQuestion ? (
          <Question
            title={foundQuestion.question}
            size={
              foundQuestion.questionType === QuestionType.VestingSharesInput ||
              foundQuestion.questionType === QuestionType.LeaversInput ||
              foundQuestion.questionType === QuestionType.CommitmentInput ||
              foundQuestion.questionType === QuestionType.ResponsibilitiesInput
                ? "small"
                : "slide"
            }
            information={filterMoreInfo(foundQuestion)}
            showMoreInfo={foundQuestion.moreInfo}
          />
        ) : null}
        <div className="flex w-100">
          <VerticalNav
            selectedPhase={selectedPhase}
            selectedSubPhase={selectedSubPhase}
            setSelectedSubPhase={setSelectedSubPhase}
          />
          <div className="flex w-100 flex-column">
            <div className="question-container">
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.SingleInput ? (
                <CompanyName
                  questionId={foundQuestion.id}
                  label={foundQuestion.label}
                />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.FoundersInput ? (
                <FoundersInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.SharesInput ? (
                <SharesInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.CommitmentInput ? (
                <CommitmentInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType ===
                QuestionType.ResponsibilitiesInput ? (
                <ResponsibilitiesInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.LeaversInput ? (
                <LeaversInput
                  questionId={foundQuestion.id}
                  titleTags={foundQuestion.title}
                  titleRight={foundQuestion.titleRight}
                  labelExplanation={foundQuestion.labelExplanation}
                />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType ===
                QuestionType.BadAfterVestingInput ? (
                <BadAfterVestingInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType ===
                QuestionType.VestingDurationInput ? (
                <VestingDurationInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.VestingStartInput ? (
                <StartDateInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.VestingSharesInput ? (
                <VestingSharesInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType ===
                QuestionType.VestingFrequencyInput ? (
                <VestingFrequencyInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.VestingCliffInput ? (
                <VestingCliffInput questionId={foundQuestion.id} />
              ) : null}
              {foundQuestion &&
              foundQuestion.questionType === QuestionType.CallOptionsInput ? (
                <CallOptionsInput
                  questionId={foundQuestion.id}
                  foundSubPhase={foundSubPhase}
                />
              ) : null}
              {finalPhase ? <Deliverables /> : null}
            </div>
            <div className="ml-auto flex">
              <div
                className={`button ${
                  selectedPhase !== 4 ? "fixed back" : ""
                } flex items-center`}
                onClick={() => {
                  if (!foundPhase) {
                    return;
                  }

                  if (!foundSubPhase) {
                    goBackWhenOnStart();
                    return;
                  }

                  if (
                    foundPhase.position === 0 &&
                    foundSubPhase.position === 0
                  ) {
                    navigate(routes.start);
                  }

                  if (allSubPhases.length === 0) {
                    return;
                  }

                  if (allSubPhases[0].id === selectedSubPhase) {
                    goBackWhenOnStart();
                  } else {
                    setSelectedSubPhase(
                      allSubPhases[foundSubPhase.position - 1].id
                    );
                  }
                }}
              >
                <FiArrowLeft />
                <div className="ml-2">Back</div>
              </div>

              {finalPhase ? null : (
                <div
                  className="button fixed next flex items-center ml-3"
                  onClick={() => {
                    if (!foundPhase) {
                      return;
                    }

                    if (allSubPhases.length === 0) {
                      return;
                    }

                    if (
                      allSubPhases[allSubPhases.length - 1].id ===
                      selectedSubPhase
                    ) {
                      goNextWhenOnEnd();
                    } else {
                      goToNextSubPhase();
                    }
                  }}
                >
                  <div className="mr-2">Next</div>
                  <FiArrowRight />
                </div>
              )}
              {finalPhase ? (
                <>
                  <GraphMaker setImage={setImage} />
                  {image !== "" ? (
                    <div
                      className="button ml-3"
                      onClick={() => {
                        setTimeout(async () => {
                          const blob = await pdf(
                            <Pdf result={result} image={image} />
                          ).toBlob();

                          const link = document.createElement("a");
                          link.href = window.URL.createObjectURL(blob);
                          link.download = `Shareholder agreement ${DateTime.utc().toFormat(
                            "dd/LL/y"
                          )}.pdf`;
                          link.click();
                        }, 1);
                      }}
                    >
                      Download PDF
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
